import type { Salutation } from '@schuelerhilfe/shared/src/enums/salutation';
import { useState } from 'react';
import type { ContactWidgetResolved } from './types';
import { submitForm } from '@/lib/api';
import { sourceUrlWithCampaign } from '@/lib/form/sourceUrl';
import type { ContactFormSubmitData } from '@/lib/form/types';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';

export type SubmittedUser = {
  salutation: Salutation;
  lastname: string;
};

export function useContactSubmit(
  formType: ContactWidgetResolved['formType'],
  contactWidgetId: number,
  pageDocumentId: string | undefined,
  pageLocale: string | undefined,
  locationSlug: string | undefined,
  submitTracking: EventTrackingResolved[] | undefined,
  successTracking: EventTrackingResolved[] | undefined,
) {
  const [submitted, setSubmitted] = useState(false);
  const [submittedUser, setSubmittedUser] = useState<SubmittedUser | null>(
    null,
  );
  const [hasError, setHasError] = useState(false);

  const handleSubmit = async (data: ContactFormSubmitData): Promise<void> => {
    setHasError(false);
    pushCmsEventTracking(submitTracking);
    try {
      await submitForm({
        type: formType,
        // Names the widget, not the recipient — the backend reads the address
        // from the widget's own CMS record (SH-455). `pageDocumentId` is the
        // stable half of the pair: `contactWidgetId` is a component row id and
        // Strapi replaces it on every publish.
        contactWidgetId,
        ...(pageDocumentId ? { pageDocumentId } : {}),
        ...(pageLocale ? { pageLocale } : {}),
        salutation: data.salutation,
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        phone: data.phone,
        locationSlug: locationSlug ?? '',
        sourceUrl: sourceUrlWithCampaign(),
        customerNumber: data.customerNumber,
      });
      pushCmsEventTracking(successTracking);
      setSubmittedUser({
        salutation: data.salutation,
        lastname: data.lastname,
      });
      setSubmitted(true);
    } catch {
      setHasError(true);
    }
  };

  return { submitted, submittedUser, hasError, handleSubmit };
}
